import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import type {
  ChecklistItem,
  ChecklistTemplateItem,
  DBProjectMeeting,
  MeetingType,
} from "@/lib/db-types";

export function useProjectMeetings(projectId: string | undefined) {
  return useQuery({
    queryKey: ["project-meetings", projectId],
    enabled: Boolean(projectId),
    queryFn: async (): Promise<DBProjectMeeting[]> => {
      const { data, error } = await supabase
        .from("project_meetings")
        .select("*")
        .eq("project_id", projectId)
        .order("held_at", { ascending: false, nullsFirst: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as DBProjectMeeting[];
    },
  });
}

export function findMeeting(
  meetings: DBProjectMeeting[] | undefined,
  type: MeetingType,
): DBProjectMeeting | null {
  return meetings?.find((m) => m.meeting_type === type) ?? null;
}

/**
 * Konverter en template (kun key + label) til en sjekkliste-rad med
 * checked=false. Brukes når en ny project_meetings-rad opprettes.
 */
export function snapshotChecklist(items: ChecklistTemplateItem[]): ChecklistItem[] {
  return items.map((it) => ({ key: it.key, label: it.label, checked: false }));
}

export interface UpsertMeetingInput {
  projectId: string;
  meetingType: MeetingType;
  status: string | null;
  reason?: string | null;
  checklist?: ChecklistItem[];
  notes?: string | null;
  heldAt?: string | null;
  whatWentWell?: string | null;
  whatWentPoorly?: string | null;
  /** Brukes ved opprettelse: hvis raden ikke finnes, snapshote disse items. */
  defaultItems?: ChecklistTemplateItem[];
}

export function useUpsertProjectMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertMeetingInput): Promise<DBProjectMeeting> => {
      // Bruker .order().limit(1) i stedet for .maybeSingle() fordi gammel data
      // kan ha flere statusmøte-rader per prosjekt (før vi flatet ut til én).
      // Vi plukker da den eldste og oppdaterer den.
      const { data: rows, error: selectErr } = await supabase
        .from("project_meetings")
        .select("*")
        .eq("project_id", input.projectId)
        .eq("meeting_type", input.meetingType)
        .order("created_at", { ascending: true })
        .limit(1);
      if (selectErr) throw new Error(selectErr.message);
      const existing = rows && rows.length > 0 ? rows[0] : null;

      const checklist =
        input.checklist ??
        (existing?.checklist as ChecklistItem[] | undefined) ??
        (input.defaultItems ? snapshotChecklist(input.defaultItems) : []);

      const row = {
        project_id: input.projectId,
        meeting_type: input.meetingType,
        status: input.status,
        reason: input.reason ?? existing?.reason ?? null,
        checklist,
        notes: input.notes ?? existing?.notes ?? null,
        held_at:
          input.heldAt ??
          existing?.held_at ??
          (input.status === "Gjennomført" ? new Date().toISOString() : null),
        what_went_well: input.whatWentWell ?? existing?.what_went_well ?? null,
        what_went_poorly: input.whatWentPoorly ?? existing?.what_went_poorly ?? null,
      };

      const query = existing
        ? supabase.from("project_meetings").update(row).eq("id", existing.id).select().single()
        : supabase.from("project_meetings").insert(row).select().single();
      const { data, error } = await query;
      if (error) throw new Error(error.message);
      return data as DBProjectMeeting;
    },
    onSettled: async (_data, _err, vars) => {
      await invalidateFor(qc, "project_meetings", { projectId: vars.projectId });
    },
  });
}

/**
 * Nullstiller (DELETE) en møte-rad for prosjektet. Brukes av "Nullstill"-knappen
 * i dialog. Returnerer void.
 */
export function useResetProjectMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      projectId: string;
      meetingType: MeetingType;
    }): Promise<void> => {
      const { error } = await supabase
        .from("project_meetings")
        .delete()
        .eq("project_id", input.projectId)
        .eq("meeting_type", input.meetingType);
      if (error) throw new Error(error.message);
    },
    onSettled: async (_data, _err, vars) => {
      await invalidateFor(qc, "project_meetings", { projectId: vars.projectId });
    },
  });
}
